import { Box, Skeleton } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { MdDownload } from 'react-icons/md';
import WarningIcon from '@/assets/icons/WarningIcon';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import { getQCodeFromProductNameAndQCode } from '@/components/Templates/BenefitVerification/BenefitVerificationTable/bv';
import { colors } from '@/components/ui/theme';
import { useGetIVRFile } from '@/hooks/service-hooks/benefitsVerification';
import type { IVRViewModalProps } from '@/types/benefitsVerification';

const IVRViewModal = ({ data, commissionableProducts }: IVRViewModalProps) => {

  const isProductCommisionable = commissionableProducts
  .map((product) => product.ta_qcode)
  .includes(
    getQCodeFromProductNameAndQCode(
      data?.priorAuthName as string,
    ),
  );

  const { priorAuthId, patientName, ta_manufacturerbvupload } = data || { priorAuthId: '', patientName: '', ta_manufacturerbvupload: null };
  const { data: ivrFileData, isLoading: isLoadingIVR } = useGetIVRFile(
    priorAuthId,
    (!!priorAuthId && isProductCommisionable)
  );

  const [fileUrl, setFileUrl] = useState<string | null>(null);

  // Reset fileUrl when priorAuthId changes
  useEffect(() => {
    setFileUrl(null);
    // If the product is not commissionable and there's a manufacturer upload link, open it in a new tab
    if(!isProductCommisionable && ta_manufacturerbvupload !== null) {
      window.open(ta_manufacturerbvupload || '', '_blank');
    }
  }, [priorAuthId]);

  useEffect(() => {
    if (ivrFileData?.data) {
      const byteArray = new Uint8Array(ivrFileData.data.data);
      const blob = new Blob([byteArray], {
        type: 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      setFileUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [ivrFileData]);

  const handleDownloadIVR = () => {
    if (fileUrl) {
      
      const filename = `${patientName}-IVR.pdf`;
      
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Box p={4}>
      <Show when={isLoadingIVR}>
        <Box>
          <Skeleton height="40px" mb={4} />
          <Skeleton height="600px" />
        </Box>
      </Show>

      <Show
        when={
          !isLoadingIVR &&
          !fileUrl &&
          !ivrFileData?.data &&
          !!data &&
          commissionableProducts.length > 0 &&
          !isProductCommisionable &&
          !ta_manufacturerbvupload
        }
      >
        <Box
          p={4}
          borderRadius="md"
          textAlign="center"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <FaceliftText fontSize="lg">
            The IVR has not been updated yet. We apologize for any inconvenience caused.
          </FaceliftText>
        </Box>
      </Show>

      <Show when={!isProductCommisionable && ta_manufacturerbvupload !== null}>
        <Box
          p={4}
          borderRadius="md"
          textAlign="center"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <FaceliftText fontSize="lg">
            Navigating to Sharepoint link provided by manufacturer.
          </FaceliftText>
        </Box>
      </Show>

      <Show when={!isLoadingIVR && !!fileUrl}>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Show when={!!ivrFileData?.notesLengthExceed}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box><WarningIcon color={colors.warning} height={36} width={36} /></Box>
                <Box fontSize="xs">
                  <FaceliftText>
                    Primary Notes: Notes cannot exceed 425 characters. Please shorten your text, or extra characters will be truncated.
                  </FaceliftText>
                  <FaceliftText>
                    Secondary Notes: Notes cannot exceed 425 characters. Please shorten your text, or extra characters will be truncated.
                  </FaceliftText>
                </Box>
              </Box>
            </Show>
            <FaceliftButton
              onClick={handleDownloadIVR}
              size="sm"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <MdDownload />
              Download
            </FaceliftButton>
          </Box>

          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            overflow="hidden"
            height="600px"
          >
            <iframe
              src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              width="100%"
              height="100%"
              title="IVR Document"
              style={{ border: 'none' }}
            />
          </Box>
        </Box>
      </Show>
    </Box>
  );
};

export default IVRViewModal;

